import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../hooks/axios';
import InputWithLabel from '../../components/form/input/InputWithLabel';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';

const initialFormData = {
  fullName: '',
  phoneNumber: '',
  email: '',
  password: '', // Only for add
  profilePictureUrl: '',
  languagePreference: '',
  currencyPreference: '',
  twoFactorEnabled: false,
};

export default function CustomerForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEditMode) {
      fetchCustomerData();
    }
  }, [id]);

  const fetchCustomerData = async () => {
    try {
      const response = await axiosInstance.get(`/customers/${id}`);
      setFormData({ ...response.data, password: '' });
    } catch (error) {
      setError('Failed to fetch customer data');
      console.error('Error fetching customer:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isEditMode) {
        const { password, ...updateData } = formData;
        await axiosInstance.put(`/customers/${id}`, updateData);
      } else {
        await axiosInstance.post('/customers', formData);
      }
      navigate('/customers');
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to save customer');
      console.error('Error saving customer:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  return (
    <div className="w-full space-y-6">
      <PageBreadcrumb pageTitle={isEditMode ? 'Edit Customer' : 'Add Customer'} />

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        {error && (
          <div className="mb-4 rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full space-y-6">
          {/* Basic Info Section */}
          <div>
            <h3 className="mb-4 text-base font-semibold text-gray-900 dark:text-white/90">Basic Information</h3>
            <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
              <InputWithLabel label="Full Name" name="fullName" value={formData.fullName} onChange={handleInputChange} required placeholder="Enter full name" />
              <InputWithLabel label="Phone Number" name="phoneNumber" value={formData.phoneNumber} onChange={handleInputChange} required placeholder="Enter phone number" />
              <InputWithLabel label="Email" name="email" value={formData.email} onChange={handleInputChange} required placeholder="Enter email" />
              {!isEditMode && (
                <InputWithLabel label="Password" name="password" value={formData.password} onChange={handleInputChange} required placeholder="Enter password" type="password" />
              )}
            </div>
          </div>

          {/* Preferences Section */}
          <div>
            <h3 className="mb-4 text-base font-semibold text-gray-900 dark:text-white/90 mt-8">Preferences</h3>
            <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
              <InputWithLabel label="Language Preference" name="languagePreference" value={formData.languagePreference} onChange={handleInputChange} placeholder="Enter language preference" />
              <InputWithLabel label="Currency Preference" name="currencyPreference" value={formData.currencyPreference} onChange={handleInputChange} placeholder="Enter currency preference" />
              <div className="flex items-center space-x-2 mt-2">
                <input
                  type="checkbox"
                  name="twoFactorEnabled"
                  checked={formData.twoFactorEnabled}
                  onChange={handleInputChange}
                  id="twoFactorEnabled"
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="twoFactorEnabled" className="text-sm font-medium text-gray-700">
                  Two Factor Enabled
                </label>
              </div>
            </div>
          </div>

          {/* Profile Section */}
          <div>
            <h3 className="mb-4 text-base font-semibold text-gray-900 dark:text-white/90 mt-8">Profile</h3>
            <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
              <InputWithLabel label="Profile Picture URL" name="profilePictureUrl" value={formData.profilePictureUrl} onChange={handleInputChange} placeholder="Enter profile picture URL" />
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/customers')}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 